import {DataLayer} from "./DataLayer";
import Enumerable from "linq";
import $ from "jquery";
import {B64File, GetFine, Ticket} from "../Api/Types";
import {Popup} from "../Popups/Popup";

type order_type_t = "str" | "number" | "parse_number" | "date";
type order_mode_t = "asc" | "desc" | "off";
type search_mode_t = "full" | "partial";
const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024; // Convert MB to Bytes

type filter_t = {
    attr: string,
    order_type: order_type_t,
    mode: order_mode_t
    search_mode: search_mode_t
}

type prop_set<T> = {
    [arg0 in keyof T]?: string;
};

export class UtilsLayer extends DataLayer {
    protected disable_css = {
        'background-color': '#ccc',
        'color': '#666',
        'cursor': 'not-allowed',
        'border': '1px solid #aaa',
    };

    protected enable_css = {
        'background-color': '#007bff',
        'color': 'white',
        'border': 'none',
        'padding': '10px 20px',
        'font-size': '16px',
        'cursor': 'pointer',
        'border-radius': '5px',
        'transition': 'background-color 0.3s'
    };

    protected enable_next_css = {
        'background-color': '#28a745',
        'color': '#fff',
        'cursor': 'pointer',
        'border': '1px solid #007bff'
    };

    protected enable_back_css = {
        'background-color': '#dc3545',
        'color': '#fff',
        'cursor': 'pointer',
        'border': '1px solid #007bff'
    };

    protected formatTime(date: Date): string {
        const hour = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');

        return `${hour}:${minutes}`;
    }

    protected formatDateData(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(date.getDate()).padStart(2, '0');

        // 3. Combine to get the desired format
        return `${year}-${month}-${day}`;
    }

    protected UpdateSearch<T extends Object>(group: T[],
                                             search_bar_box: JQuery,
                                             filter_source: JQuery,
                                             extra_input: JQuery): Enumerable.IEnumerable<T> {
        if (group.length === 0)
            return Enumerable.from([]);

        let filters: filter_t[] = [];
        let off_filters = 0;

        let must_contains = search_bar_box.find("input").val().toString().toLowerCase();
        for (const x of filter_source.find("i")) {
            const order_mode = x.getAttribute("data-filter-status");
            const search_mode = x.getAttribute("data-search-mode") ?? "partial";
            if (order_mode === void 0)
                return Enumerable.from([]);

            if (order_mode === "off")
                ++off_filters;

            filters.push({
                attr: x.getAttribute("data-filter-attr"),
                mode: order_mode as order_mode_t,
                order_type: x.getAttribute("data-filter-order-type") as order_type_t,
                search_mode: search_mode as search_mode_t
            })
        }

        const filter_val = (x: T, filter: filter_t, date_as_string: boolean = false) => {
            // @ts-ignore
            const val: string | Date | number = x[filter.attr];
            if (val === null)
                return null;

            if (filter.order_type === "date")
                return date_as_string ? (<Date>val).toLocaleString() : (<Date>val).getTime();
            else if (["str", "number"].includes(filter.order_type))
                return val;
            else
                return Number((<string>val).match(/\d+/).toString());
        }

        const search_results = search_bar_box.find(".researched").hide();

        const extra_data_filer: prop_set<T> = {};

        for (let e of extra_input) {
            const i = $(e);
            const ctx = {a: i.attr("data-extra-search-arg") as string, v: i.val() as string};

            const k = group[0];
            if (!(ctx.a in k)) {
                console.warn("Ignoring", e);
                continue;
            }

            if (ctx.v === "sp:any" || !ctx.v)
                continue;

            if (ctx.v === "sp:0r")
                return Enumerable.from([]);

            extra_data_filer[ctx.a as keyof T] = ctx.v;
        }

        let counter: JQuery = void 0;
        if (must_contains !== "" || extra_data_filer)
            counter = search_results.show().find('[data-content-type="found"]').text(0);

        let c = 0;
        let sorted_group = Enumerable.from(group);
        if (extra_data_filer) {
            sorted_group = sorted_group.where(
                x => {
                    for (let item in extra_data_filer) {
                        if (x[item] != extra_data_filer[item])
                            return false;
                    }

                    if (must_contains === "")
                        counter?.text(++c);

                    return true;
                }
            );
        }

        if (must_contains !== "")
            sorted_group = sorted_group.where(
                x => {
                    for (const filter of filters) {
                        const val = <string | number>filter_val(x, filter, true);
                        // @ts-ignore
                        x["__cached_filter" + filter.attr] = val;

                        if (val === null)
                            continue;
                        const string_val = val.toString().toLowerCase();

                        if ((filter.search_mode === "full" && string_val === must_contains) ||
                            (filter.search_mode === "partial" && string_val.includes(must_contains))) {
                            counter?.text(++c);
                            return true;
                        }
                    }
                    return false;
                }
            )

        if (filters.length - off_filters === 0)
            return sorted_group;

        let first = true;
        for (const filter of filters) {
            if (filter.mode === "off")
                continue;

            if (first) {
                sorted_group = sorted_group.orderBy(x => filter_val(x, filter));
                first = false;
            } else {
                // @ts-ignore
                sorted_group = (<Enumerable.IOrderedEnumerable<T>>sorted_group).thenBy(x => x["__cached_filter" + filter.attr] ?? filter_val(x, filter));
            }

            // @ts-ignore
            sorted_group.descending = filter.mode === "desc";
        }

        return sorted_group;
    }

    protected setSearchEvents<T>(selector: string,
                                 filter_s: string,
                                 source_getter: () => T[],
                                 on_update: (search: Enumerable.IEnumerable<T>) => void,
                                 extra_input?: JQuery): () => void {
        const filter_source = $(filter_s);
        const search_bar_box = $(selector);

        extra_input ??= $();

        const update = (element: T[]) => on_update(this.UpdateSearch(element, search_bar_box, filter_source, extra_input));
        let x: NodeJS.Timeout = null;

        filter_source.on("click", "div", e => {
            const i = $(e.currentTarget).find("i");
            const seq = ["off", "asc", "desc"];
            const pos = (seq.indexOf(i.attr("data-filter-status")) + 1) % 3;
            filter_source.find("[data-filter-status!=off]").attr("data-filter-status", "off")
            i.attr("data-filter-status", seq[pos]);

            update(source_getter())

            if (x !== null)
                clearTimeout(x);
        });

        let last_search: string = null;

        search_bar_box.find<HTMLInputElement>("input").on("input", e => {
            const elements = source_getter();

            if (x !== null)
                clearTimeout(x);


            if (elements.length < 500 ||
                (last_search !== null && Math.abs(e.currentTarget.value.length - last_search.length) !== 1)
            ) {
                x = null;
                setTimeout(() => update(elements), 0);
                last_search = e.currentTarget.value;
                return;
            }
            last_search = e.currentTarget.value;

            x = setTimeout(() => {
                x = null;
                update(elements);
            }, 400);
        });

        return () => update(source_getter());
    }

    protected setUrlParameter(name: string, value: string) {
        this.url_params.set(name, value);
        window.history.replaceState(null, null, "?" + this.url_params.toString());
    }

    protected formatCurrency(value: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value).replace('€', '').trim();
    };

    protected CreateTicketPopUp(container: JQuery, fine: GetFine|null = null): void {
        // alert("create ticket popup");
        const faq_template = $(DataLayer.templates["open-ticket-popup-template"]);
        // $('#assistance').append(faq_template)
        container.append(faq_template)

        faq_template.on('submit', (e: JQuery.SubmitEvent) => {
            e.preventDefault(); // Prevent the default form submission

            // Get the file inputs and read the files if present
            const fileInput1 = $("#attached_file")[0] as HTMLInputElement;
            const fileInput2 = $("#attached_file2")[0] as HTMLInputElement;

            const file1 = fileInput1?.files ? fileInput1.files[0] : null;
            const file2 = fileInput2?.files ? fileInput2.files[0] : null;

            const errors: string[] = [];

            // if ((file1 && file1.size > MAX_FILE_SIZE_BYTES) || (file2 && file2.size > MAX_FILE_SIZE_BYTES))
            //     errors.push($(DataLayer.templates["file-too-big-description"]).text());

            const reader1 = new FileReader();
            const reader2 = new FileReader();

            // Define the structure for the attachments
            let attachment1: { b64: string, file_name: string, mime_type: string } | null = null;
            let attachment2: { b64: string, file_name: string, mime_type: string } | null = null;

            // Counter to track the number of file readers that have finished processing
            let filesProcessed = 0;
            const isValidName = (name: string): string | null => {
                if (name === "") {
                    return $(DataLayer.templates["ticket-name-empty-template"]).text();
                } else if (name.length < 4) {
                    return $(DataLayer.templates["ticket-name-too-short-template"]).text();
                } else if (name.length > 128) {
                    return $(DataLayer.templates["ticket-name-too-long-template"]).text();
                }
                return null;
            };

            const isValidSubject = (subject: string): string | null => {
                if (subject === "")
                    return $(DataLayer.templates["subject-empty-template"]).text();
                else if (subject.length < 4)
                    return $(DataLayer.templates["subject-too-short-template"]).text();
                else if (subject.length > 128)
                    return $(DataLayer.templates["subject-too-long-template"]).text();
            };

            const isValidEmail = (email: string): string | null => {
                if (email === "")
                    return $(DataLayer.templates["email-empty-template"]).text();
                else if (!this.email_regex)
                    return $(DataLayer.templates["invalid-email-short-template"]).text();
                else if (email.length > 128)
                    return $(DataLayer.templates["email-too-long-template"]).text();
            };

            const isValidAtachment = (file: File, number: string): string | null => {

                if(file && file.size > MAX_FILE_SIZE_BYTES)
                    return $(DataLayer.templates[`file-${number}-too-big-description`]).text();
                else console.log(`good attach ${number}`);
            }


            const isValidMessage = (message: string): string | null => {
                if (message === "")
                    return $(DataLayer.templates["message-empty-template"]).text();
                else if (message.length > 128)
                    return $(DataLayer.templates["message-too-long-template"]).text();
            }

            // Function to handle payload creation once both files are processed
            const createPayloadAndSend = () => {
                if (filesProcessed === 2 || (!file1 && !file2)) {
                    const name_inp = $("#name")
                    const mail_inp = $("#email")
                    const subject_inp = $("#subject")
                    const message_inp = $("#message")
                    const name_val = name_inp.val().toString().trim().replace(/\s+/g, " ");
                    const mail_val = mail_inp.val().toString().trim().replace(/\s+/g, " ");
                    const subject_val = subject_inp.val().toString().trim().replace(/\s+/g, " ");
                    const message_val = message_inp.val().toString().trim().replace(/\s+/g, " ");
                    const verbal_id = fine.verbal_id

                    // const errors: string[] = [];

                    const nameError = isValidName(name_val);
                    if (nameError) errors.push(nameError);

                    const emailError = isValidEmail(mail_val);
                    if (emailError) errors.push(emailError);

                    const subjectError = isValidSubject(subject_val);
                    if (subjectError) errors.push(subjectError);

                    const messageError = isValidMessage(message_val);
                    if (messageError) errors.push(messageError);

                    const fileOneError = isValidAtachment(file1, 'one');
                    if (fileOneError) errors.push(fileOneError);

                    const fileTwoError = isValidAtachment(file2, 'two');
                    if (fileTwoError) errors.push(fileTwoError);

                    if (errors.length > 0) {
                        const error_message = "<div>" + errors.join("<div></div>") + "</div>"
                        const error = $(DataLayer.safe_get_template("general-error")).text();

                        const t = Popup.DefaultErrorInject(error_message, error, true);
                        const set_animation = (d: JQuery.Deferred<any>) => {
                            d.fail(() => t.popup.ShowError("WRONGGGGGGGGG"));
                            d.done(() => {alert(); t.popup.Remove()});

                            d.always((x: JQuery.Deferred<any>) => set_animation(x))
                        }

                        set_animation(t.deferred);
                    } else {
                        // Call API to create ticket once both files are processed (or if no files)
                        this.client.create_ticket(
                            $("#name").val() as string,
                            $("#email").val() as string,
                            $("#subject").val() as string,
                            $("#message").val() as string,
                            attachment1,
                            attachment2,
                            verbal_id
                        ).done((x: Ticket) => {
                            faq_template.remove(); // Remove form after submission
                            // open(x.link); // Handle response (optional)
                            this.resultPopup(x);
                        }).fail(e => {
                            console.error("Error creating ticket:", e);
                        });
                    }
                }
            };

            // File reader load end event for file 1
            reader1.onloadend = () => {
                if (file1) {
                    const base64 = (reader1.result as string).split(',')[1]; // Extract base64 part
                    attachment1 = {
                        b64: base64,
                        file_name: file1.name,
                        mime_type: file1.type  // Get MIME type
                    };
                }

                filesProcessed++; // Increment processed counter
                createPayloadAndSend(); // Attempt to create the payload if both files are processed
            };

            // File reader load end event for file 2
            reader2.onloadend = () => {
                if (file2) {
                    const base64 = (reader2.result as string).split(',')[1]; // Extract base64 part
                    attachment2 = {
                        b64: base64,
                        file_name: file2.name,
                        mime_type: file2.type  // Get MIME type
                    };
                }

                filesProcessed++; // Increment processed counter
                createPayloadAndSend(); // Attempt to create the payload if both files are processed
            };

            // Read file 1 as Data URL if present
            if (file1) {
                reader1.readAsDataURL(file1);
            } else {
                filesProcessed++; // Increment the counter if file 1 is absent
            }

            // Read file 2 as Data URL if present
            if (file2) {
                reader2.readAsDataURL(file2);
            } else {
                filesProcessed++; // Increment the counter if file 2 is absent
            }

            // Trigger payload creation if no files are selected
            createPayloadAndSend();
        });

        faq_template.find('#close-ticket').on('click', () => {
            faq_template.remove();
            // alert("chiudi ticket");
        })
    }

    private resultPopup(ticket: Ticket) {
        const ticketResponseTemplate = $(DataLayer.templates["open-ticket-popup-response-template"]);
        ticketResponseTemplate.find('#close-ticket-details').on('click', () => {
            ticketResponseTemplate.remove();
        })
        ticketResponseTemplate.find('[data-content-type="ticket-response-ticket-title"]').text(ticket.title);
        ticketResponseTemplate.find('[data-content-type="ticket-response-ticket-body"]').text(ticket.body);
        ticketResponseTemplate.find('[data-content-type="ticket-response-ticket-link"]').prop('href', ticket.link);
        ticketResponseTemplate.find('[data-content-type="ticket-response-ticket-number"]').text(ticket.number);
        $("body").append(ticketResponseTemplate);
    }

    protected HandleDownload(file: B64File) {
        const url = URL.createObjectURL(new Blob([file.b64], {type: file.mime_type}));
        const link = document.createElement("a");
        link.href = url;
        link.download = file.file_name;
        link.click();
    }

    protected BuildLangDropdown() {
        const models: {
            [lang: string]: {
                name: string,
                flag: string
            }
        } = JSON.parse($(DataLayer.safe_get_template("lang-drop-down-data")).text());

        const toggle_expansion = () => $('#lang-selector .dropdown-menu').toggleClass("show");

        const update_lang = (lang: keyof typeof models) => {
            const model = models[lang];
            $<HTMLImageElement>("#dropdown-toggle-img").prop("src", model.flag);
            $("#dropdown-toggle-span").text(model.name);
            $("#selected-language").val(lang);
            toggle_expansion();

            if (location.search.indexOf("page=login") === -1) {

                this.client.change_langauge(lang.toString()).done(() => {
                    location.reload();
                })
            }
        };

        $("#lang-selector .dropdown-toggle").on("click", () => toggle_expansion());

        $("#lang-selector .dropdown-item").on("click", function () {
            const lang = <keyof typeof models>this.getAttribute("data-lang");
            update_lang(lang);
        });
    }
}